export interface StickyModeState {
  topPinned: boolean;
  bottomPinned: boolean;
  stickyScale: number;
}

export interface StickyModeInput {
  diagramType: 'flow' | 'sequence';
  topHeaderScreenY: number;
  bottomHeaderScreenY: number;
  viewportHeight: number;
  stickyTopOffset: number;
  viewportScale: number;
  stickyBottomInset?: number;
  headerHeight?: number;
}

const TOP_ENTER_MARGIN = 0;
const TOP_EXIT_MARGIN = 18;
const BOTTOM_ENTER_MARGIN = 8;
const BOTTOM_EXIT_MARGIN = 28;
const STICKY_SCALE_MIN = 0.72;
const STICKY_SCALE_MAX = 1;
const DEFAULT_HEADER_HEIGHT = 44;
const DEFAULT_BOTTOM_INSET = 12;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function computeStickyScale(viewportScale: number): number {
  if (!Number.isFinite(viewportScale) || viewportScale <= 0) return STICKY_SCALE_MAX;
  const normalized = 1 / Math.max(1, viewportScale);
  return clamp(normalized, STICKY_SCALE_MIN, STICKY_SCALE_MAX);
}

export function screenToCanvasY(screenY: number, viewportY: number, viewportScale: number): number {
  const safeScale = Math.max(0.0001, viewportScale);
  return (screenY - viewportY) / safeScale;
}

/** Visible canvas-space rect for the current viewport. Used to size overlay shapes. */
export function visibleCanvasRect(
  viewport: { x: number; y: number; scale: number },
  windowSize: { width: number; height: number },
  pad = 2,
): { x: number; y: number; width: number; height: number } {
  const scale = Math.max(0.0001, viewport.scale);
  return {
    x: (-viewport.x / scale) - pad,
    y: (-viewport.y / scale) - pad,
    width: windowSize.width / scale + pad * 2,
    height: windowSize.height / scale + pad * 2,
  };
}

export function canvasToScreenY(canvasY: number, viewportY: number, viewportScale: number): number {
  return canvasY * viewportScale + viewportY;
}

export function computeStickyHeaderScreenHeight(viewportScale: number, stickyScale: number, headerHeight: number = DEFAULT_HEADER_HEIGHT): number {
  return Math.max(1, headerHeight * viewportScale * stickyScale);
}

export interface StickyMaskBandsInput {
  state: StickyModeState;
  stickyTopOffset: number;
  viewportHeight: number;
  stickyBottomInset?: number;
  headerScreenHeight: number;
}

export interface StickyMaskBand {
  y: number;
  height: number;
}

export function computeStickyMaskBandScreenRects(input: StickyMaskBandsInput): { top: StickyMaskBand | null; bottom: StickyMaskBand | null } {
  const bandHeight = input.headerScreenHeight + 20;
  const top = input.state.topPinned
    ? { y: input.stickyTopOffset - 8, height: bandHeight }
    : null;
  const bottomHeaderTop = input.viewportHeight - (input.stickyBottomInset ?? DEFAULT_BOTTOM_INSET) - input.headerScreenHeight;
  const bottom = input.state.bottomPinned
    ? { y: bottomHeaderTop - 8, height: bandHeight }
    : null;
  return { top, bottom };
}

export function resolveStickyMode(prev: StickyModeState, input: StickyModeInput): StickyModeState {
  if (input.diagramType !== 'sequence') {
    return { topPinned: false, bottomPinned: false, stickyScale: STICKY_SCALE_MAX };
  }

  const stickyScale = computeStickyScale(input.viewportScale);
  const headerScreenHeight = computeStickyHeaderScreenHeight(
    input.viewportScale,
    stickyScale,
    input.headerHeight ?? DEFAULT_HEADER_HEIGHT,
  );
  const bottomInset = input.stickyBottomInset ?? DEFAULT_BOTTOM_INSET;
  const bottomHeaderPinnedY = input.viewportHeight - bottomInset - headerScreenHeight;

  const topPinned = prev.topPinned
    ? input.topHeaderScreenY < input.stickyTopOffset + TOP_EXIT_MARGIN
    : input.topHeaderScreenY < input.stickyTopOffset + TOP_ENTER_MARGIN;

  const bottomPinned = prev.bottomPinned
    ? input.bottomHeaderScreenY > bottomHeaderPinnedY - BOTTOM_EXIT_MARGIN
    : input.bottomHeaderScreenY > bottomHeaderPinnedY + BOTTOM_ENTER_MARGIN;

  return { topPinned, bottomPinned, stickyScale };
}
